import { bindActionCreators } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { noop, omit } from 'lodash';
import { Component, createRef } from 'react';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { uploadNewVersion } from '~/api/document';
import { DOC_VIEW_PAGE_LAYOUT_BUTTON_GROUP_TESTID } from '~/common/constants/testids/documents-view-page';
import {
  createDocumentProvision,
  deleteDocument,
  deleteDocumentTable,
  getDocumentAlgoStatus,
  getDocumentInformationSections,
  getDocumentProvisions,
  getDocumentViewPageData,
  renameDocument,
  reprocessDocument,
  updateDocumentProvision,
  updateDocumentTable,
} from '~/components/DocumentsViewPage/Document.data';
import CommentHighlightRenderProps from '~/components/DocumentsViewPage/DocumentContentsPanel/CommentHighlightRenderProps';
import DocumentContentsPanel from '~/components/DocumentsViewPage/DocumentContentsPanel/DocumentContentsPanel';
import DocumentKeyProvisions from '~/components/DocumentsViewPage/DocumentKeyProvisions/DocumentKeyProvisions';
import { DocumentOverviewPanelConditional } from '~/components/DocumentsViewPage/DocumentOverviewPanelConditional';
import DocumentRelatesPanel from '~/components/DocumentsViewPage/DocumentRelatesPanel/DocumentRelatesPanel';
import DocumentsNavigation from '~/components/DocumentsViewPage/DocumentsNavigation/DocumentsNavigation';
import { DocumentsViewPageProvider } from '~/components/DocumentsViewPage/DocumentsViewPage.context';
import styles from '~/components/DocumentsViewPage/DocumentsViewPage.styles';
import DocumentVersions from '~/components/DocumentsViewPage/DocumentVersions/DocumentVersions';
import DocumentViewHeader from '~/components/DocumentsViewPage/DocumentViewHeader/DocumentViewHeader';
import {
  getDocumentTypes,
  getTabIndexWithinRange,
} from '~/components/DocumentsViewPage/utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import AppLayout from '~/components/Shared/AppLayout/AppLayout';
import { Comments } from '~/components/Shared/AppLayout/SidebarContent/Comments/Comments';
import { filterHighlightsOfDifferentVersion } from '~/components/Shared/AppLayout/SidebarContent/Comments/utils';
import { ColumnLayoutButtonGroup } from '~/components/Shared/ColumnLayoutButtonGroup/ColumnLayoutButtonGroup';
import { DocumentDetailsPanel } from '~/components/Shared/DocumentDetailsPanel/DocumentDetailsPanel';
import { PdfHighlighterProvider } from '~/components/Shared/EcPdfViewerHighlighter/components/PdfHighlighterContext';
import {
  EcTab,
  EcTabList,
  EcTabPanel,
  EcTabs,
} from '~/components/Shared/EcTabs';
import { showToast } from '~/components/Shared/EcToast/showToast';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import WarningIcon from '~/components/Shared/Icons/WarningIcon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageSidebar } from '~/eds/src/components/PageSidebar/PageSidebar';
import { AlgorithmStatusType } from '~/enums/AlgorithmStatusType';
import { EntityType } from '~/enums/EntityType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { HighlightSourceType } from '~/enums/HighlightSourceType';
import { HighlightType } from '~/enums/HighlightType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { LayoutType } from '~/enums/LayoutType';
import { QueryParamType } from '~/enums/QueryParamType';
import { ChatBot } from '~/features/ask-anything/chatbot/ChatBot';
import { withAskAiSidepanel } from '~/features/ask-anything/hooks/withAskAiSidepanel';
import { RedirectToXRayModal } from '~/features/x-ray/modals/RedirectToXRayModal';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withPermissions } from '~/hocs/withPermissions';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import {
  getFolderHandlersByPosition,
  getFolderPositionByHandler,
} from '~/redux/api/methods/search';
import { slices } from '~/redux/slices';
import {
  fillCurrentIndexList,
  initContext,
  resetNavigation,
  selectItem as selectNavigationItem,
  setCurrentDocumentAction,
  setIndexById as setNavigationIndexById,
  verifyHandlerId,
} from '~/redux/slices/documentsNavigation';
import { uploadDocumentsThunk } from '~/redux/thunks/fileUpload';
import { withRouting } from '~/routing/withRouting';
import { redirectToFolder } from '~/services/redirects';
import { ERROR, INFO, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { getStorageItem, setStorageItem } from '~/utils/browser';
import { copyToClipboard } from '~/utils/helper.utils';
import { parseNavigationResponse } from '~/utils/navigation';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';
import { getClientInfo, getUserClientInfo } from '~/utils/user';

const CONTENTS_ACTIVE_TAB = 1;
const KEY_CLAUSES_TAB = 2;
const MAX_TAB_INDEX = 4;
const VERSIONS_TAB_INDEX = 3;
const docGroupViewPermission = {
  resourceId: 'document_groups',
  resourceType: 'view',
};
const conversationalAiPermission = {
  resourceId: 'conversational_ai',
  resourceType: 'edit',
};
const permissionsToCheck = [docGroupViewPermission, conversationalAiPermission];
class Page extends Component {
  constructor(props) {
    super(props);

    this.onDocumentDelete = this.onDocumentDelete.bind(this);
    this.onDocumentRename = this.onDocumentRename.bind(this);
    this.loadDocumentData = this.loadDocumentData.bind(this);
    this.refreshDocumentVersions = this.refreshDocumentVersions.bind(this);
    this.loadDocumentProvisions = this.loadDocumentProvisions.bind(this);
    this.handleUpdateProvision = this.handleUpdateProvision.bind(this);
    this.handleRemoveProvision = this.handleRemoveProvision.bind(this);
    this.handleUpdateProvisionTable =
      this.handleUpdateProvisionTable.bind(this);
    this.handleRemoveProvisionTable =
      this.handleRemoveProvisionTable.bind(this);
    this.handleSelectMessageSource = this.handleSelectMessageSource.bind(this);
    this.updateSingleField = this.updateSingleField.bind(this);
    this.getContextEntities = this.getContextEntities.bind(this);

    this.documentsNavigationRef = createRef();

    this.state = {
      documentId: props.params.documentId,
      document: {},
      OCRProcessing: {},
      processingInterval: null,
      htmlData: null,
      loading: true,
      informationSections: [],
      activePanel: null,
      dateModified: null,
      documentProvisions: [],
      documentTables: [],
      tabIndex: 0,
      sources: [],
      activeSourceIndex: 0,
      clauseId: null,
      isWssContract: false,
    };
  }

  componentDidMount() {
    const { flags } = this.props;

    this.checkOCRStatus();
    const processingInterval = setInterval(this.checkOCRStatus, 3000);
    this.setState({ processingInterval });

    const layout = getPageSearchQueryByKey(
      QueryParamType.Layout,
      getStorageItem(QueryParamType.Layout) || LayoutType.TwoColumn,
    );
    this.props.setContentTabLayout(layout);
    setStorageItem(QueryParamType.Layout, layout);
    updatePageSearchQuery({ [QueryParamType.Layout]: layout });

    const activeTab = getPageSearchQueryByKey(
      QueryParamType.ActiveTab,
      CONTENTS_ACTIVE_TAB,
    );
    const tabIndexWithinLimit = getTabIndexWithinRange(
      activeTab,
      MAX_TAB_INDEX,
    );

    const initialActivePanel = flags[FlagType.DocumentDetailsInPostSig]
      ? 'documentDetails'
      : null;

    this.setState(
      { tabIndex: tabIndexWithinLimit, activePanel: initialActivePanel },
      () => {
        updatePageSearchQuery({
          [QueryParamType.ActiveTab]: tabIndexWithinLimit,
        });
        this.loadDocumentData();
      },
    );
    this.loadDocumentProvisions();
  }

  componentWillUnmount() {
    clearInterval(this.state.processingInterval);
    const { pathname } = this.props.location;
    const { dispatch } = this.props;
    if (pathname !== '/analyzer/results') {
      this.props.analyzerResetData();
    }
    //reset navigators, TODO: remove on final navigation version implemented
    const isDocumentViewPage = /document\/\d+/.test(pathname);
    if (!isDocumentViewPage) {
      this.props.resetNavigation();
    }

    dispatch(setCurrentDocumentAction(undefined));
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.params.documentId !== prevState.documentId) {
      return { documentId: nextProps.params.documentId };
    } else {
      return null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const CONTENT_TAB_INDEX = 1;
    if (prevState.documentId !== this.state.documentId) {
      this.loadDocumentData();
      this.loadDocumentProvisions();
    } else if (
      this.isPdfPreviewHighlighting() &&
      (prevState.tabIndex !== this.state.tabIndex ||
        this.state.document !== prevState.document) &&
      this.state.tabIndex === CONTENT_TAB_INDEX
    ) {
      /** This verification is a temporary solution and aims to show a toast to the user informing that
       *  the provisions and key information coordinates are being calculated.
       *  This will only be shown to the first user accessing a document without coordinates and the calculation
       * is performed only once. We may remove this after all old document have their coordinates calculated. */
      this.showCoordinatesAreBeingCalculatedToast();
    } else if (
      prevProps.askAiSidepanel.isVisible !==
        this.props.askAiSidepanel.isVisible &&
      this.state.activePanel === 'chatbot'
    ) {
      this.setState({ activePanel: null });
    }
  }

  checkOCRStatus = async () => {
    const { dispatch, intl } = this.props;
    const { pathname } = this.props.location;
    try {
      const docResponse = await getDocumentAlgoStatus(this.state.documentId);
      if (docResponse.algo_status.OCR.status === AlgorithmStatusType.Success) {
        dispatch(slices.pages.actions.clearPageBanner({ pathname }));
        if (
          this.state.OCRProcessing.status === AlgorithmStatusType.InProgress
        ) {
          showToast(
            SUCCESS,
            intl.formatMessage({
              id: 'DocumentViewPage.documentProcessedSuccess',
              defaultMessage: 'The document has finished processing',
              description: 'Success message when document processing completes',
            }),
          );
        }
      }
      if (
        docResponse.algo_status.OCR.status !== AlgorithmStatusType.InProgress &&
        !docResponse.processing_status.includes('QUEUED')
      ) {
        clearInterval(this.state.processingInterval);
      }
      this.setState({
        OCRProcessing: {
          // in certain contexts BE does not return an object for failed state
          status:
            docResponse.algo_status.OCR === -1
              ? -1
              : docResponse.algo_status.OCR.status,
          message: docResponse.algo_status.OCR.message,
          allowReprocess: docResponse.algo_status.allow_reprocess,
          queued: docResponse.processing_status.includes('QUEUED'),
        },
      });
    } catch (e) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DocumentViewPage.documentStatusError',
          defaultMessage:
            'An error occurred while fetching the document status.',
          description: 'Error message when document status fetch fails',
        }),
      );
      if (
        e?.response?.status === HttpStatusCodeType.Forbidden ||
        e?.response?.status === HttpStatusCodeType.NotFound
      ) {
        // clear polling interval if user does not have permission to view the document
        clearInterval(this.state.processingInterval);
      }
    }
  };

  handleLayoutChange = (layout) => {
    this.props.setContentTabLayout(layout);
    setStorageItem(QueryParamType.Layout, layout);
    updatePageSearchQuery({ [QueryParamType.Layout]: layout });

    const { currentUser } = this.props;
    trackSegment('User Sets Document Content Tab Layout', {
      layout,
      groupId: getClientInfo(currentUser),
      userId: getUserClientInfo(currentUser),
    });
  };

  errorHandler(error) {
    const { intl } = this.props;
    let errorMessage = intl.formatMessage({
      id: 'DocumentViewPage.generalLoadError',
      defaultMessage: 'An error occurred while loading this document.',
      description: 'General error message for document loading failures',
    });

    if (error) {
      if (404 === error?.response?.status) {
        errorMessage = intl.formatMessage({
          id: 'DocumentViewPage.documentNotFound',
          defaultMessage: "The document you're looking for cannot be found.",
          description: 'Error message when document is not found (404)',
        });
      }
      if (HttpStatusCodeType.Forbidden === error?.response?.status) {
        errorMessage = intl.formatMessage({
          id: 'DocumentViewPage.noPermissionError',
          defaultMessage: 'You do not have permission to view this document.',
          description:
            'Error message when user lacks permission to view document',
        });
      }
      this.setState({
        errorLoadingDocument: true,
        loading: false,
        errorMessage,
      });
    }
  }

  loadDocumentData() {
    const { documentId } = this.state;
    const { dispatch } = this.props;

    this.setState({ loading: true });
    getDocumentViewPageData(
      documentId,
      async (
        documentRes,
        documentContentRes,
        informationSectionsRes,
        documentTableRes,
      ) => {
        dispatch(
          setCurrentDocumentAction({
            id: documentRes.document,
            versionId: documentRes?.current_version?.id,
            handlerId: documentRes.id,
            name: documentRes?.current_version?.file_name,
            type: documentRes?.current_version?.file_type,
            folder: documentRes?.parent_folder,
          }),
        );
        this.setState({
          document: documentRes,
          htmlData: documentContentRes.html
            .replace(/\\"/g, '"')
            .replace(/<br\/>/gi, ''),
          informationSections: informationSectionsRes.sections,
          integrationUrls: {
            procurementContractUrl:
              informationSectionsRes.procurement_contract_url,
          },
          dateModified: informationSectionsRes.date_modified,
          loading: false,
          userVisibilityLevel: documentRes.visibility_level,
          isWssContract: informationSectionsRes.is_wss_contract,
          documentTables: documentTableRes,
        });

        if (
          !this.props.navigationContext?.isInitialized &&
          documentRes?.parent_folder
        ) {
          try {
            await this.initializeNavigationFromFolder(
              documentRes.parent_folder,
            );
          } catch (error) {
            console.error('Failed to initialize navigation context:', error);
          }
        }
      },
      (error) => {
        this.errorHandler(error);
      },
    );
  }

  initializeNavigationFromFolder = async (folderId) => {
    const { initContext } = this.props;

    const sortedColumn = getPageSearchQueryByKey(
      QueryParamType.SortedColumn,
      'name',
    );
    const sortedOrder = getPageSearchQueryByKey(
      QueryParamType.SortedOrder,
      'asc',
    );

    const api = (position, folder) =>
      getFolderHandlersByPosition({
        position,
        folderId: folder,
        orderByField: sortedColumn,
        order: sortedOrder,
      });
    const getPosition = (handler, folder) =>
      getFolderPositionByHandler({
        handlerId: handler,
        folderId: folder,
        orderByField: sortedColumn,
        order: sortedOrder,
      });

    const initOptions = {
      api,
      name: 'documents',
      parseResponse: parseNavigationResponse,
      getPosition,
      folderId,
    };
    await initContext(initOptions);
  };

  loadDocumentProvisions() {
    const { documentId } = this.state;
    const { params } = this.props;

    getDocumentProvisions(documentId)
      .then((documentProvisions) => {
        const provision = documentProvisions.find(
          (provision) => provision.name === params.provisionName,
        );
        const id = provision ? provision.content.clauseId : null;

        this.setState({ clauseId: id });
        this.setState({ documentProvisions });
      })
      .catch((error) => {
        this.errorHandler(error);
      });
  }

  handleTabClick = (activeTabIndex) => {
    if (activeTabIndex === CONTENTS_ACTIVE_TAB) {
      this.loadDocumentProvisions();
    }

    this.setState({ tabIndex: activeTabIndex, clauseIdToScroll: null }, () =>
      updatePageSearchQuery({ [QueryParamType.ActiveTab]: activeTabIndex }),
    );
  };

  handleNavigateToClauseTab = (id) => {
    this.handleTabClick(KEY_CLAUSES_TAB);
    this.setState({ clauseIdToScroll: id });
  };

  shouldDisableSources = (message) => {
    return (
      message.sources?.[0]?.coordinates === null &&
      this.isPdfPreviewHighlighting()
    );
  };

  handleSelectMessageSource = (message, sourceIndex) => {
    if (this.shouldDisableSources(message)) {
      // Nuance PDF highlights not supported
      return;
    }
    if (this.state.tabIndex !== CONTENTS_ACTIVE_TAB) {
      this.setState({ tabIndex: CONTENTS_ACTIVE_TAB }, () => {
        updatePageSearchQuery({
          [QueryParamType.ActiveTab]: CONTENTS_ACTIVE_TAB,
        });
      });
    }

    this.setState({
      sources: this.transformSources(message.sources),
      activeSourceIndex: sourceIndex,
    });

    trackSegment('selectChatMessageSource', {
      messageId: message.id,
      sourceIndex,
    });
  };

  transformSources = (_sources) => {
    const { document } = this.state;
    const sources =
      document.current_version.file_type === FileExtensionType.Pdf
        ? _sources.filter(
            (source) => source.coordinates && source.coordinates.length,
          )
        : _sources;

    if (sources?.length > 1) {
      const htmlTokenList = [];

      for (let source of sources) {
        htmlTokenList.push(source.htmlTokens);
      }
      return sources.map((source, index) => ({
        ...source,
        html_tokens: htmlTokenList,
        name: `Source ${index + 1} of ${sources.length}`,
        type: HighlightType.Source,
      }));
    } else {
      return sources.map((source) => ({
        ...source,
        html_tokens: [source.htmlTokens],
      }));
    }
  };

  async updateSingleField() {
    const { params, intl } = this.props;
    try {
      const informationSections = await getDocumentInformationSections(
        params.documentId,
      );
      const documentTypes = getDocumentTypes(informationSections.sections);
      const newDocument = {
        ...this.state.document,
        document_types: documentTypes,
      };
      this.setState({
        document: newDocument,
        informationSections: informationSections.sections,
      });
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DocumentViewPage.fieldEditError',
          defaultMessage: 'An error occurred while editing the field(s).',
          description: 'Error message when field editing fails',
        }),
      );
      this.loadDocumentData();
    }
  }

  onDocumentDelete(reason, extraDetails) {
    const { currentUser, intl } = this.props;
    const { document } = this.state;

    deleteDocument(document.id, reason, extraDetails).then(() => {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'DocumentViewPage.documentDeletedSuccess',
          defaultMessage: 'Document has been deleted.',
          description: 'Success message when document is deleted',
        }),
      );
      redirectToFolder(
        currentUser.client_config.sub_domain,
        document.parent_folder,
      );
    });
  }

  onDocumentRename(documentName, parentId) {
    const { document } = this.state;
    const { intl } = this.props;

    renameDocument(document.id, documentName, parentId)
      .then(() => {
        this.onRenameSuccess(documentName);
      })
      .catch((err) => {
        if (err?.response?.data?.non_field_errors?.[0]) {
          showToast(ERROR, err.response.data.non_field_errors[0]);
        } else if (err.response.status === 422) {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentViewPage.invalidDocumentName',
              defaultMessage: 'Cannot have "/" in a document name',
              description:
                'Error message for invalid document name with forward slash',
            }),
          );
        } else {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentViewPage.documentRenameError',
              defaultMessage: 'An error occurred while editing the document.',
              description: 'Error message when document rename fails',
            }),
          );
        }
      });
  }

  onRenameSuccess = (newName) => {
    const newDocument = { ...this.state.document };
    newDocument.document_name = newName;
    this.setState({ document: newDocument });
    this.loadDocumentData();
  };

  handleNewVersionUpload = async (file) => {
    const { documentId } = this.state;
    const { intl } = this.props;
    try {
      await uploadNewVersion({ documentId, file });
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'DocumentViewPage.newVersionUploadSuccess',
          defaultMessage: 'New uploaded version has been successfully created.',
          description: 'Success message when new document version is uploaded',
        }),
      );

      this.checkOCRStatus();
      const processingInterval = setInterval(this.checkOCRStatus, 3000);
      this.setState({ processingInterval });

      this.loadDocumentData();
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DocumentViewPage.newVersionUploadError',
          defaultMessage: 'An error occurred while uploading the new version.',
          description: 'Error message when new version upload fails',
        }),
      );
    }
  };

  handleCreateProvisionPdf = (
    provisionName,
    position,
    content,
    selectedApplyTo,
    query,
  ) => {
    const { documentId } = this.state;
    const sampleName = `${provisionName?.substring(0, 20)}_${format(
      new Date(),
      'yyyyMMddHHmmss',
    )}`;

    const coordinates = [
      {
        source: HighlightSourceType.Tagging,
        coordinates: omit(position, 'boundingRect'),
      },
    ];
    const params = {
      documentId,
      provisionName,
      coordinates,
      content,
      selectedApplyTo,
      sampleName,
      query,
    };
    return this.createProvision(params);
  };

  handleCreateProvisionText = (
    provisionName,
    htmlTokens,
    content,
    selectedApplyTo,
    query,
  ) => {
    const { documentId } = this.state;

    //l10n-ignore: API request no formatting required
    const sampleName = `${provisionName?.substring(0, 20)}_${format(
      new Date(),
      'yyyyMMddHHmmss',
    )}`;

    const params = {
      documentId,
      provisionName,
      htmlTokens,
      content,
      selectedApplyTo,
      sampleName,
      query,
    };

    return this.createProvision(params);
  };

  createProvision = (params) => {
    const { flags, dispatch, intl } = this.props;
    const { clauseId } = this.state;
    const hasQuickAiFiltersFlag = flags[FlagType.QuickAiFilters];

    return createDocumentProvision(params)
      .then(() => {
        const applyToAllDocs = Boolean(params.selectedApplyTo);
        const clauseNameParam = `${QueryParamType.Clause}=${encodeURIComponent(
          params.provisionName,
        )}`;
        const clauseParam = `${QueryParamType.clauseId}=${clauseId}`;
        const enableQuickAiFilters = applyToAllDocs && hasQuickAiFiltersFlag;

        this.loadDocumentProvisions();
        dispatch(api.util.invalidateTags([TAGS.TAG_CLAUSES]));
        showToast(
          SUCCESS,
          enableQuickAiFilters
            ? intl.formatMessage({
                id: 'DocumentViewPage.clauseCreatedWithLink',
                defaultMessage:
                  'The clause has been successfully created. Click here to go to your clause',
                description:
                  'Success message with link when clause is created and applied to all documents',
              })
            : intl.formatMessage({
                id: 'DocumentViewPage.clauseCreatedSuccess',
                defaultMessage: 'The clause has been successfully created.',
                description: 'Success message when clause is created',
              }),
          enableQuickAiFilters
            ? `/automation/detail?&${clauseNameParam}&${clauseParam}`
            : null,
        );
      })

      .catch((err) => {
        if (
          err &&
          err.response &&
          err.response.data.non_field_errors &&
          err.response.data.non_field_errors[0]
        ) {
          showToast(ERROR, err.response.data.non_field_errors[0]);
        } else {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentViewPage.clauseCreateError',
              defaultMessage: 'An error occurred while creating the clause.',
              description: 'Error message when clause creation fails',
            }),
          );
        }
      });
  };

  handleUpdateProvision(
    provisionId,
    clauseId,
    selectedProvisionName,
    htmlTokens,
    content,
    selectedApplyTo,
  ) {
    const { documentId } = this.state;
    const { intl } = this.props;

    return updateDocumentProvision(
      documentId,
      provisionId,
      clauseId,
      selectedProvisionName,
      htmlTokens,
      content,
      selectedApplyTo,
    )
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'DocumentViewPage.clauseUpdatedSuccess',
              defaultMessage:
                'The clause "{selectedProvisionName}" has been successfully saved.',
              description:
                'Success message when clause is updated. selectedProvisionName is the name of the clause',
            },
            {
              selectedProvisionName,
            },
          ),
        );
        this.loadDocumentProvisions();
      })
      .catch((err) => {
        if (
          err &&
          err.response &&
          err.response.data.non_field_errors &&
          err.response.data.non_field_errors[0]
        ) {
          showToast(ERROR, err.response.data.non_field_errors[0]);
        } else {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentViewPage.clauseUpdateError',
              defaultMessage: 'An error occurred while updating the clause.',
              description: 'Error message when clause update fails',
            }),
          );
        }
      });
  }

  handleRemoveProvision(provisionId, selectedProvisionName) {
    const { documentId } = this.state;
    const { dispatch, intl } = this.props;

    return dispatch(
      api.endpoints.deleteDocumentClause.initiate({
        documentHandlerId: documentId,
        clauseId: provisionId,
      }),
    )
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'DocumentViewPage.clauseDeletedSuccess',
              defaultMessage:
                'The clause "{selectedProvisionName}" has been successfully deleted.',
              description:
                'Success message when clause is deleted. selectedProvisionName is the name of the clause',
            },
            {
              selectedProvisionName,
            },
          ),
        );
        this.loadDocumentProvisions();
      })
      .catch((err) => {
        if (
          err &&
          err.response &&
          err.response.data.non_field_errors &&
          err.response.data.non_field_errors[0]
        ) {
          showToast(ERROR, err.response.data.non_field_errors[0]);
        } else {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentViewPage.clauseDeleteError',
              defaultMessage: 'An error occurred while deleting the clause.',
              description: 'Error message when clause deletion fails',
            }),
          );
        }
      });
  }

  handleUpdateProvisionTable(tableId, tableContent) {
    const { document, documentTables } = this.state;
    const { intl } = this.props;

    updateDocumentTable(document.id, tableId, tableContent)
      .then((res) => {
        const tableIndex = documentTables.results
          .map((table) => table.id)
          .indexOf(tableId);
        const newDocumentTables = { ...documentTables };
        newDocumentTables.results[tableIndex] = res;
        this.setState({ documentTables: newDocumentTables });
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'DocumentViewPage.tableUpdatedSuccess',
            defaultMessage: 'Table has been updated.',
            description: 'Success message when table is updated',
          }),
        );
      })
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'DocumentViewPage.tableUpdateError',
            defaultMessage: 'An error occurred while updating this table.',
            description: 'Error message when table update fails',
          }),
        ),
      );
  }

  handleRemoveProvisionTable(tableId) {
    const { document, documentTables } = this.state;
    const { intl } = this.props;

    deleteDocumentTable(document.id, tableId)
      .then(() => {
        const newTables = [
          ...documentTables.results.filter((table) => table.id !== tableId),
        ];
        this.setState({
          documentTables: { ...documentTables, results: [...newTables] },
        });
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'DocumentViewPage.tableDeletedSuccess',
            defaultMessage: 'Table has been deleted.',
            description: 'Success message when table is deleted',
          }),
        );
      })
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'DocumentViewPage.tableDeleteError',
            defaultMessage: 'An error occurred while deleting this table.',
            description: 'Error message when table deletion fails',
          }),
        ),
      );
  }

  isPdfPreviewHighlighting = () => {
    const { document } = this.state;
    const filetypesWithPdfPreviewHighlighting = [
      FileExtensionType.Pdf,
      FileExtensionType.Jpg,
      FileExtensionType.Jpeg,
      FileExtensionType.Png,
      FileExtensionType.Tif,
      FileExtensionType.Tiff,
    ];
    return (
      document &&
      filetypesWithPdfPreviewHighlighting.includes(document.file_type)
    );
  };

  refreshDocumentVersions() {
    this.loadDocumentData();
    this.setState({ tabIndex: VERSIONS_TAB_INDEX }, () => {
      updatePageSearchQuery({ [QueryParamType.ActiveTab]: VERSIONS_TAB_INDEX });
    });
  }

  onCopyLink(comment) {
    const linkConstructed = `${window.location.origin}${window.location.pathname}?commentId=${comment.id}&activeTab=1`;
    copyToClipboard(linkConstructed, undefined, undefined, undefined, false);
  }

  onHighlightClick(highlightId, highlightProps) {
    if (this.state.tabIndex !== CONTENTS_ACTIVE_TAB) {
      this.setState({ tabIndex: CONTENTS_ACTIVE_TAB }, () => {
        updatePageSearchQuery({
          [QueryParamType.ActiveTab]: CONTENTS_ACTIVE_TAB,
        });
      });
    }
    highlightProps.setActiveHighlight(highlightId, true);
  }

  showCoordinatesAreBeingCalculatedToast = () => {
    const { document } = this.state;
    const { intl } = this.props;
    if (document && document.is_processing_coordinates)
      showToast(
        INFO,
        intl.formatMessage({
          id: 'DocumentViewPage.coordinatesProcessingInfo',
          defaultMessage:
            'Some AI output clauses and key information are still loading in the background. Please refresh your page after 2 minutes.',
          description:
            'Info message when document coordinates are being processed',
        }),
        null,
        10000,
      );
  };

  onCloseSidebar(commentHighlightRenderProps) {
    const { dispatch, flags } = this.props;
    commentHighlightRenderProps.setActiveHighlight(null);
    commentHighlightRenderProps.setActiveCommentId(null);

    if (
      this.state.activePanel === 'documentDetails' &&
      flags[FlagType.DocumentDetailsInPostSig]
    ) {
      dispatch(
        slices.documentsViewPage.actions.setDocumentDetailsPanelOpen(false),
      );
    }

    this.setState({ activePanel: null });
  }

  getContextEntities() {
    const { document, documentId } = this.state;
    const contextEntities = [];
    if (document?.current_version?.id) {
      contextEntities.push({
        id: document?.current_version?.id
          ? String(document?.current_version?.id)
          : null,
        type: EntityType.DocumentVersion,
      });
    }

    if (documentId) {
      contextEntities.push({
        id: documentId,
        type: EntityType.DocumentHandler,
      });
    }
    return contextEntities;
  }

  openConversationAIPanel = ({ isAskAiButton = false }) => {
    const { document } = this.state;
    const { dispatch, flags } = this.props;
    const context = this.getContextEntities();
    const documentVersionEntity = context.find(
      (context) => context.type === EntityType.DocumentVersion,
    );

    const documentHandlerEntity = context.find(
      (context) => context.type === EntityType.DocumentHandler,
    );
    const hasVersionId = Boolean(document?.current_version?.id);
    const entity = hasVersionId ? documentVersionEntity : documentHandlerEntity;
    const hasAskAnything = flags[FlagType.AskAnything];

    if (hasAskAnything) {
      dispatch(slices.chatbot.actions.toggleChatbot());
    } else {
      this.setState({ activePanel: 'chatbot' });
    }

    trackSegment(isAskAiButton ? 'selectAskAI' : 'selectChat', {
      name: isAskAiButton ? 'Ask AI' : 'Conversational AI',
      entity,
    });
  };

  displayBanner({ status, message, allowReprocess, queued }) {
    const { dispatch, intl } = this.props;
    const { pathname } = this.props.location;

    if (status === AlgorithmStatusType.InProgress || queued) {
      dispatch(
        slices.pages.actions.setPageBanner({
          banner: {
            message: intl.formatMessage({
              id: 'DocumentViewPage.processingBanner',
              defaultMessage:
                'Evisort AI is processing this document. It will be available for editing in a few minutes.',
              description:
                'Banner message shown when document is being processed',
            }),
            status: 'ai',
          },
          pathname,
        }),
      );
    } else if (status === AlgorithmStatusType.Failed) {
      dispatch(
        slices.pages.actions.setPageBanner({
          banner: {
            message,
            status: 'danger',
            action: allowReprocess
              ? {
                  text: intl.formatMessage({
                    id: 'DocumentViewPage.reprocessButton',
                    defaultMessage: 'Process document again',
                    description: 'Button text for reprocessing failed document',
                  }),
                  tooltip: intl.formatMessage({
                    id: 'DocumentViewPage.reprocessTooltip',
                    defaultMessage: 'Process document again',
                    description: 'Tooltip for reprocess button',
                  }),
                  onClick: async () => {
                    await reprocessDocument(this.state.documentId);
                    this.checkOCRStatus();
                    const processingInterval = setInterval(
                      this.checkOCRStatus,
                      3000,
                    );
                    this.setState({ processingInterval });
                  },
                }
              : null,
          },
          pathname,
        }),
      );
    }
  }

  render() {
    const activeCommentIdFromUrl = getPageSearchQueryByKey(
      QueryParamType.CommentId,
      null,
    );

    const {
      classes,
      currentUser,
      flags,
      navigationContext,
      isChatbotVisible,
      sources,
      activeSourceIndex,
      askAIEntity,
      intl,
      askAiSidepanel,
      dispatch,
    } = this.props;

    const hasConversationalAIPermission = this.props.checkPermission(
      conversationalAiPermission.resourceId,
      conversationalAiPermission.resourceType,
    );

    const enableConversationalAI = flags[FlagType.ConversationalAI];
    const hasAskAnything = flags[FlagType.AskAnything];
    const hasDocumentDetailsPanelUpdate =
      flags[FlagType.DocumentDetailsPanelUpdate];
    const {
      clauseIdToScroll,
      document,
      documentHighlight,
      OCRProcessing,
      loading,
      errorLoadingDocument,
      htmlData,
      informationSections,
      integrationUrls,
      activePanel,
      dateModified,
      documentProvisions,
      documentId,
      userVisibilityLevel,
      documentTables,
      tabIndex,
      isWssContract,
    } = this.state;

    const disableChat =
      OCRProcessing.status === AlgorithmStatusType.Failed ||
      OCRProcessing.status === AlgorithmStatusType.InProgress;

    const documentEntity = document.document
      ? {
          id: document.document,
          type: EntityType.Document,
        }
      : {};

    const isTerminated = document?.doc_status === 'TERMINATED';

    if (
      OCRProcessing.status === AlgorithmStatusType.InProgress ||
      OCRProcessing.status === AlgorithmStatusType.Failed
    ) {
      this.displayBanner(OCRProcessing);
    }

    const contextEntities = this.getContextEntities();

    const documentVersionEntity = contextEntities.find(
      (context) => context.type === EntityType.DocumentVersion,
    );

    const documentHandlerEntity = contextEntities.find(
      (context) => context.type === EntityType.DocumentHandler,
    );

    const provisionsForRender = documentProvisions.map((item) => {
      const contentText =
        item.content && item.content.length
          ? item.content.map((content) => (
              <p key={content.id} id={content.id}>
                {content.text}
              </p>
            ))
          : '';
      return {
        content: [contentText],
        name: item.name,
      };
    });

    const renderDocumentView = (
      commentHighlightRenderProps,
      permissionRenderProps,
    ) => {
      return (
        <div className={classes.documentViewPage}>
          {!errorLoadingDocument && (
            <DocumentViewHeader
              user={currentUser}
              userVisibilityLevel={userVisibilityLevel}
              documentId={documentId}
              classes={classes}
              documentData={document}
              loading={loading}
              integrationUrls={integrationUrls}
              OCRProcessing={OCRProcessing}
              handleDocumentDelete={this.onDocumentDelete}
              handleDocumentRename={this.onDocumentRename}
              refreshDocumentVersions={this.refreshDocumentVersions}
              isWssContract={isWssContract}
              askAiButtonProps={{
                onClick: () =>
                  this.openConversationAIPanel({ isAskAiButton: true }),
                disabled: disableChat,
                tooltip: getChatTooltip(OCRProcessing.status, intl),
              }}
            />
          )}
          {loading ? (
            <div className={classes.loadingContainer}>
              <LoadingSpinner size="medium" />
            </div>
          ) : errorLoadingDocument ? (
            <div className={classes.errorLoadingDocument}>
              <WarningIcon size="24" red />
              <span>{this.state.errorMessage}</span>
            </div>
          ) : (
            <div className={classes.documentContents}>
              <EcTabs
                className={classes.documentTabs}
                selectedIndex={tabIndex}
                onSelect={this.handleTabClick}
              >
                <FlexLayout>
                  <FlexLayout flex="auto">
                    <EcTabList className={classes.documentTabList}>
                      <EcTab>
                        {intl.formatMessage({
                          id: 'DocumentViewPage.overviewTab',
                          defaultMessage: 'Overview',
                          description: 'Overview tab label',
                        })}
                      </EcTab>
                      <EcTab>
                        {intl.formatMessage({
                          id: 'DocumentViewPage.contentsTab',
                          defaultMessage: 'Contents',
                          description: 'Contents tab label',
                        })}
                      </EcTab>
                      <EcTab>
                        {intl.formatMessage({
                          id: 'DocumentViewPage.keyClausesTab',
                          defaultMessage: 'Key Clauses',
                          description:
                            'Key Clauses tab label when edit clause flag is enabled',
                        })}
                      </EcTab>
                      <EcTab>
                        {intl.formatMessage({
                          id: 'DocumentViewPage.versionsTab',
                          defaultMessage: 'Versions',
                          description: 'Versions tab label',
                        })}
                      </EcTab>
                      {!flags[FlagType.GroupedTicketsAndDocs] &&
                        this.props.checkPermission(
                          docGroupViewPermission.resourceId,
                          docGroupViewPermission.resourceType,
                        ) && (
                          <EcTab>
                            {intl.formatMessage({
                              id: 'DocumentViewPage.relatedDocumentsTab',
                              defaultMessage: 'Related Documents',
                              description: 'Related Documents tab label',
                            })}
                          </EcTab>
                        )}
                    </EcTabList>
                  </FlexLayout>
                  <FlexLayout flex="none" alignItems="center" space={4}>
                    <DocumentsNavigation
                      ref={this.documentsNavigationRef}
                      context={navigationContext}
                      documentId={documentId}
                      fillCurrentIndexList={this.props.fillCurrentIndexList}
                      verifyHandlerId={this.props.verifyHandlerId}
                      location={window.document.location}
                      selectItem={selectNavigationItem}
                      setIndexById={this.props.setNavigationIndexById}
                    />

                    {!flags[FlagType.DocumentDetailsInPostSig] &&
                      tabIndex === 1 && (
                        <ColumnLayoutButtonGroup
                          className={DOC_VIEW_PAGE_LAYOUT_BUTTON_GROUP_TESTID}
                          onChange={this.handleLayoutChange}
                          layout={this.props.contentTabLayout}
                        />
                      )}
                  </FlexLayout>
                </FlexLayout>

                <EcTabPanel>
                  <DocumentOverviewPanelConditional
                    OCRProcessing={OCRProcessing}
                    documentId={documentId}
                    userVisibilityLevel={userVisibilityLevel}
                    isTerminated={isTerminated}
                    sections={informationSections}
                    dateModified={dateModified}
                    onEditSubmitted={this.loadDocumentData}
                  />
                </EcTabPanel>
                <EcTabPanel forceRender>
                  <DocumentsViewPageProvider
                    value={{
                      document: { id: document.id },
                      refreshField: this.updateSingleField,
                    }}
                  >
                    <DocumentContentsPanel
                      user={currentUser}
                      userVisibilityLevel={userVisibilityLevel}
                      isTerminated={isTerminated}
                      documentHandlerId={document.id}
                      documentId={document.document}
                      documentVersionId={document.current_version.id}
                      fileType={document.file_type}
                      handleNavigateToClauseTab={this.handleNavigateToClauseTab}
                      htmlData={htmlData}
                      sections={informationSections}
                      onCreateProvision={
                        this.isPdfPreviewHighlighting()
                          ? this.handleCreateProvisionPdf
                          : this.handleCreateProvisionText
                      }
                      onUpdateProvision={this.handleUpdateProvision}
                      onRemoveProvision={this.handleRemoveProvision}
                      documentProvisions={documentProvisions}
                      OCRStatus={
                        OCRProcessing?.status !== undefined
                          ? OCRProcessing.status
                          : document.algo_status.OCR.status
                      }
                      queued={OCRProcessing.queued}
                      displaySideInfoIn2Cols={
                        this.props.contentTabLayout === LayoutType.ThreeColumn
                      }
                      //highlights props
                      sources={
                        sources.length && isChatbotVisible
                          ? this.transformSources(sources)
                          : this.state.sources
                      }
                      activeSourceIndex={
                        isChatbotVisible && activeSourceIndex !== null
                          ? activeSourceIndex
                          : this.state.activeSourceIndex
                      }
                      documentHighlight={documentHighlight}
                      //commentHighlightRenderProps is undefined in case FeatureFlagType.PostSigActivityFeed is OFF
                      {...commentHighlightRenderProps}
                      onCreateComment={
                        commentHighlightRenderProps
                          ? commentHighlightRenderProps.onCreateComment
                          : noop
                      }
                      setIsDocumentLoading={
                        commentHighlightRenderProps
                          ? commentHighlightRenderProps.setIsDocumentLoading
                          : noop
                      }
                      setActiveHighlight={
                        commentHighlightRenderProps
                          ? commentHighlightRenderProps.setActiveHighlight
                          : noop
                      }
                      //permissionRenderProps is undefined in case FeatureFlagType.PostSigActivityFeed is OFF
                      {...permissionRenderProps}
                    />
                  </DocumentsViewPageProvider>
                </EcTabPanel>
                <EcTabPanel>
                  <DocumentKeyProvisions
                    documentId={documentId}
                    user={currentUser}
                    rebateTables={documentTables.results}
                    keyProvisions={provisionsForRender}
                    clauseIdToScroll={clauseIdToScroll}
                    paymentTables={document.payment_table}
                    handleTableUpdate={this.handleUpdateProvisionTable}
                    handleTableDelete={this.handleRemoveProvisionTable}
                  />
                </EcTabPanel>
                <EcTabPanel>
                  <DocumentVersions
                    user={currentUser}
                    documentId={documentId}
                    userVisibilityLevel={userVisibilityLevel}
                    reloadDocumentContent={this.loadDocumentData}
                  />
                </EcTabPanel>
                {!flags[FlagType.GroupedTicketsAndDocs] &&
                  this.props.checkPermission(
                    docGroupViewPermission.resourceId,
                    docGroupViewPermission.resourceType,
                  ) && (
                    <EcTabPanel>
                      <DocumentRelatesPanel
                        documentHandlerId={documentId}
                        documentId={document.document}
                        reloadDocumentContent={this.loadDocumentData}
                      />
                    </EcTabPanel>
                  )}
              </EcTabs>
            </div>
          )}
          <RedirectToXRayModal />
        </div>
      );
    };

    const hasVersionId = Boolean(document?.current_version?.id);
    const conversationalAIEntity = hasVersionId
      ? documentVersionEntity
      : documentHandlerEntity;
    const chatbotEntity = hasAskAnything ? askAIEntity : conversationalAIEntity;

    const shouldShowNavigation = navigationContext?.currentIndex >= 0;
    const shouldDisablePrevious = navigationContext?.currentIndex <= 0;
    const shouldDisableNext =
      navigationContext?.currentIndex >= navigationContext?.total - 1;

    const { isVisible: isAskAiSidepanelVisible, width: askAiSidepanelWidth } =
      askAiSidepanel;

    return (
      <PdfHighlighterProvider>
        <CommentHighlightRenderProps
          activeCommentId={activeCommentIdFromUrl}
          entity={documentEntity}
          context={contextEntities}
          highlightEntity={documentVersionEntity}
          render={(commentHighlightRenderProps) => (
            <AppLayout
              sidebar={
                <PageSidebar
                  isExpanded={
                    Boolean(commentHighlightRenderProps.activeCommentId) ||
                    activePanel ||
                    isAskAiSidepanelVisible
                  }
                  panels={{
                    chatbot:
                      enableConversationalAI && hasConversationalAIPermission
                        ? {
                            action: {
                              disabled: disableChat,
                              icon: 'evisort-ai-static',
                              label: intl.formatMessage({
                                id: 'DocumentViewPage.chatbotLabel',
                                defaultMessage: 'chatbot',
                                description: 'Chatbot sidebar panel label',
                              }),
                              tooltip: getChatTooltip(
                                OCRProcessing.status,
                                intl,
                              ),
                              value: 'chatbot',
                            },
                            component:
                              chatbotEntity && !hasAskAnything ? (
                                <ChatBot
                                  entity={chatbotEntity}
                                  shouldDisableSources={
                                    this.shouldDisableSources
                                  }
                                  onSelectMessageSource={
                                    this.handleSelectMessageSource
                                  }
                                />
                              ) : (
                                <Layout id="layout" />
                              ),
                          }
                        : {},
                    ...(flags[FlagType.DocumentDetailsInPostSig] ||
                    hasDocumentDetailsPanelUpdate
                      ? {
                          documentDetails: {
                            action: {
                              icon: 'field-file',
                              label: intl.formatMessage({
                                id: 'DocumentViewPage.documentDetailsLabel',
                                defaultMessage: 'document details',
                                description:
                                  'Document details sidebar panel label',
                              }),
                              tooltip: intl.formatMessage({
                                id: 'DocumentViewPage.documentDetailsTooltip',
                                defaultMessage: 'Document Details',
                                description:
                                  'Document details sidebar panel tooltip',
                              }),
                              value: 'documentDetails',
                            },
                            component: (
                              <DocumentDetailsPanel
                                isEmbedded
                                actionsConfig={
                                  hasDocumentDetailsPanelUpdate
                                    ? undefined
                                    : {
                                        navigate: { hidden: true },
                                        next: {
                                          onClick: () => {
                                            this.documentsNavigationRef?.current?.handleNext();
                                          },
                                          hidden: !shouldShowNavigation,
                                          disabled: shouldDisableNext,
                                        },
                                        previous: {
                                          onClick: () => {
                                            this.documentsNavigationRef?.current?.handlePrev();
                                          },
                                          hidden: !shouldShowNavigation,
                                          disabled: shouldDisablePrevious,
                                        },
                                        upload: {
                                          hidden: false,
                                          onSuccess:
                                            this.handleNewVersionUpload,
                                        },
                                        rename: {
                                          onSuccess: this.onRenameSuccess,
                                        },
                                        delete: {
                                          onSuccess: () =>
                                            redirectToFolder(
                                              currentUser.client_config
                                                .sub_domain,
                                              document.parent_folder,
                                            ),
                                        },
                                      }
                                }
                                documentName={
                                  hasDocumentDetailsPanelUpdate
                                    ? intl.formatMessage({
                                        id: 'DocumentViewPage.documentDetailsTooltip',
                                        defaultMessage: 'Document Details',
                                        description:
                                          'Document details sidebar panel tooltip',
                                      })
                                    : undefined
                                }
                                hideAllActions={hasDocumentDetailsPanelUpdate}
                                docHandlerId={documentId}
                                onFieldHighlight={(field) =>
                                  this.setState({
                                    tabIndex: CONTENTS_ACTIVE_TAB,
                                    documentHighlight: {
                                      field: {
                                        ...field,
                                        html_tokens: field.htmlTokens,
                                      },
                                    },
                                  })
                                }
                                onClauseHighlight={(clause) =>
                                  this.setState({
                                    tabIndex: CONTENTS_ACTIVE_TAB,
                                    documentHighlight: { clause },
                                  })
                                }
                                enableFieldEditing={!isTerminated}
                                expandWidth="680px"
                              />
                            ),
                          },
                        }
                      : {}),
                    comments: {
                      action: {
                        icon: 'message',
                        label: intl.formatMessage({
                          id: 'DocumentViewPage.commentsLabel',
                          defaultMessage: 'comments',
                          description: 'Comments sidebar panel label',
                        }),
                        tooltip: intl.formatMessage({
                          id: 'DocumentViewPage.commentsTooltip',
                          defaultMessage: 'Comments',
                          description: 'Comments sidebar panel tooltip',
                        }),
                        value: 'comments',
                      },
                      component: (
                        <Comments
                          activeCommentId={
                            commentHighlightRenderProps.activeCommentId
                          }
                          activeHighlightId={
                            commentHighlightRenderProps.activeHighlightId
                          }
                          entity={documentEntity}
                          context={contextEntities}
                          filterComment={(comment) =>
                            filterHighlightsOfDifferentVersion(comment, {
                              type: documentVersionEntity?.type,
                              id: documentVersionEntity?.id,
                            })
                          }
                          highlightResolver={
                            commentHighlightRenderProps.highlightResolver
                          }
                          isExpanded={Boolean(
                            commentHighlightRenderProps.activeCommentId,
                          )}
                          onCopyLink={this.onCopyLink}
                          onUpdateFilters={
                            commentHighlightRenderProps.onUpdateFilters
                          }
                          onHighlightClick={(highlight) =>
                            this.onHighlightClick(
                              highlight,
                              commentHighlightRenderProps,
                            )
                          }
                        />
                      ),
                    },
                  }}
                  selectedPanel={
                    isAskAiSidepanelVisible
                      ? 'chatbot'
                      : commentHighlightRenderProps.activeCommentId
                        ? 'comments'
                        : activePanel
                  }
                  //Only override width for Ask AI panel, other panels use their own panel's width.
                  width={
                    isAskAiSidepanelVisible ? askAiSidepanelWidth : undefined
                  }
                  onClose={() => {
                    this.onCloseSidebar(commentHighlightRenderProps);
                    if (isAskAiSidepanelVisible) {
                      dispatch(slices.chatbot.actions.toggleChatbot());
                    }
                  }}
                  onUpdate={(updatedPanel) => {
                    const { flags } = this.props;
                    if (isAskAiSidepanelVisible) {
                      dispatch(slices.chatbot.actions.toggleChatbot());
                    }
                    switch (updatedPanel) {
                      case 'comments':
                        if (
                          this.state.activePanel === 'documentDetails' &&
                          flags[FlagType.DocumentDetailsInPostSig]
                        ) {
                          dispatch(
                            slices.documentsViewPage.actions.setDocumentDetailsPanelOpen(
                              false,
                            ),
                          );
                        }
                        this.setState({ activePanel: 'comments' });
                        break;
                      case 'chatbot':
                        if (
                          this.state.activePanel === 'documentDetails' &&
                          flags[FlagType.DocumentDetailsInPostSig]
                        ) {
                          dispatch(
                            slices.documentsViewPage.actions.setDocumentDetailsPanelOpen(
                              false,
                            ),
                          );
                        }
                        this.openConversationAIPanel({
                          isAskAiButton: false,
                        });
                        this.setState({ activePanel: 'chatbot' });
                        break;
                      case 'documentDetails':
                        if (flags[FlagType.DocumentDetailsInPostSig]) {
                          dispatch(
                            slices.documentsViewPage.actions.setDocumentDetailsPanelOpen(
                              true,
                            ),
                          );
                        }
                        this.setState({ activePanel: 'documentDetails' });
                        break;
                      default:
                        break;
                    }
                  }}
                />
              }
            >
              {renderDocumentView(commentHighlightRenderProps, {})}
            </AppLayout>
          )}
        />
      </PdfHighlighterProvider>
    );
  }
}

const mapStateToProps = ({
  currentUser,
  documentsNavigation: { context: navigationContext },
  documentsViewPage: { contentTabLayout },
  chatbot: { sources, activeSourceIndex, isVisible, entity },
}) => ({
  currentUser,
  contentTabLayout,
  navigationContext,
  sources,
  activeSourceIndex,
  isChatbotVisible: isVisible,
  askAIEntity: entity,
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        analyzerResetData: slices.analyzer.actions.resetData,
        fillCurrentIndexList,
        initContext,
        verifyHandlerId,
        setContentTabLayout:
          slices.documentsViewPage.actions.setContentTabLayout,
        setNavigationIndexById,
        resetNavigation,
        uploadDocumentsThunk,
      },
      dispatch,
    ),
  };
}

const getChatTooltip = (status, intl) => {
  const baseTooltip = intl.formatMessage({
    id: 'DocumentViewPage.askAiTooltip',
    defaultMessage: 'Ask AI',
    description: 'Base tooltip for Ask AI button',
  });
  switch (status) {
    case AlgorithmStatusType.Failed:
      return (
        baseTooltip +
        '\n' +
        intl.formatMessage({
          id: 'DocumentViewPage.documentFailedTooltip',
          defaultMessage: '(Document failed to process)',
          description:
            'Additional tooltip text when document processing failed',
        })
      );
    case AlgorithmStatusType.InProgress:
      return (
        baseTooltip +
        '\n' +
        intl.formatMessage({
          id: 'DocumentViewPage.documentProcessingTooltip',
          defaultMessage: '(Document is still processing)',
          description:
            'Additional tooltip text when document is still processing',
        })
      );
    default:
      return baseTooltip;
  }
};

Page = connect(mapStateToProps, mapDispatchToProps)(Page);

export default withPermissions(
  injectSheet(styles)(
    withRouting(withFlags(withAskAiSidepanel(injectIntl(Page)))),
  ),
  permissionsToCheck,
);
